<template>
  <b-form @submit.prevent="submitForm">
    <b-form-group>
      <template v-slot:label>
        {{ $t("subscriptionToRegister") }} <span class="text-danger">*</span>
      </template>
      <treeselect
        v-model="subscriptionPlanId"
        :options="subscriptionPlanOptions"
        :placeholder="$t('chooseSubscription')"
        @input="subscriptionChange"
        :clearable="false"
        :searchable="true"
      />
    </b-form-group>
    <b-form-group>
      <template v-slot:label>{{ $t("serviceStartDate") }} <span class="text-danger">*</span></template>
      <b-form-datepicker v-model="startTime" required :min="new Date()" no-flip hide-header
        label-no-date-selected="DD/MM/YYYY" locale="vi" :date-format-options="datePickerFormatOptions"
        start-weekday="1"></b-form-datepicker>
    </b-form-group>
    <template v-if="shouldShowPricing">
      <template v-if="selectedSubscriptionPlan.managementType === enumManagementType.CONCURRENT_PARTICIPANT_POOL">
        <b-form-group>
          <template v-slot:label>
            {{ $t("maxParticipantPool") }}
          </template>
          <b-form-input v-model="meetingConcurrentParticipantsLimit" type="number" min="0" required />
        </b-form-group>
      </template>
      <template v-else>
        <b-form-group>
          <template v-slot:label>
            {{ $t("numberOfConcurrentMeetings") }}
          </template>
          <b-form-input v-model="numberOfConcurrentMeetings" type="number" min="0" required />
        </b-form-group>
         <b-form-group>
          <template v-slot:label>
            {{ $t("numOfParticipants") }}
          </template>
          <b-form-input v-model="meetingConcurrentParticipantsLimit" type="number" min="0" required />
        </b-form-group>
      </template>
      <div class="border-bottom pb-3 mb-3">
        <label>{{ $t("subscriptionInfo") }}</label>
        <b-form-row>
          <b-col cols="6">{{ $t("subscriptionLength") }}</b-col>
          <b-col cols="6"><b>{{ selectedSubscriptionPlan.numberOfDaysUsage }} {{ selectedSubscriptionPlan.unitTimeVN
              }}</b></b-col>
        </b-form-row>
        <b-form-row v-for="(row, idx) in planInfoRows" :key="idx">
          <b-col cols="6">{{ $t(row.label) }}</b-col>
          <b-col cols="6">
            <b>{{ row.value }}</b>
            <b v-if="row.suffix" style="margin-left: 4px;">{{ $t(row.suffix) }}</b>
          </b-col>
        </b-form-row>
      </div>
      <div class="mb-3">
        <label>{{ $t("serviceInfo") }}</label>
        <b-form-row>
          <b-col cols="6">{{ $t("expDate") }}</b-col>
          <b-col cols="6"><b>{{ expDateStr }}</b></b-col>
        </b-form-row>
        <b-form-row>
          <b-col cols="6">{{ $t("servicePrice") }}</b-col>
          <b-col cols="6"><b>{{ priceStr }} VND</b></b-col>
        </b-form-row>
        <b-form-row>
          <b-col cols="6">{{ $t("vatTax") }}</b-col>
          <b-col cols="6"><b>{{ taxAmountStr }} VND</b></b-col>
        </b-form-row>
        <b-form-row>
          <b-col cols="6">{{ $t("amountCustomerToPay") }}</b-col>
          <b-col cols="6"><b>{{ dueAmountStr }} VND</b></b-col>
        </b-form-row>
      </div>
      <p class="text-secondary">{{ $t("registerConfirmCorrectInfo") }}</p>
       <div v-if="formErrorMsg" class="text-danger">{{ formErrorMsg }}</div>
      <div class="text-right mt-4">
        <b-button type="button" variant="secondary" class="mr-3" @click="onClickCancel" :disabled="isSubmitting">{{
          $t("cancel") }}</b-button>
        <b-button type="submit" variant="primary" :disabled="isSubmitting">{{ $t("registerService") }}</b-button>
      </div>
    </template>
  </b-form>
</template>
<style lang="scss" scoped>
/* Đảm bảo z-index của treeselect cao hơn modal nếu cần */
::v-deep .vue-treeselect__menu-container {
  z-index: 9999 !important;
}
</style>
<script lang="ts">
import { Component, Emit, Prop } from 'vue-property-decorator';
import _ from "lodash";
import moment, { Moment } from "moment";
import BigNumber from "bignumber.js";
import BaseComponent from "@/components/BaseComponent";
import * as subscriptionPlanService from "@/service/subscriptionPlanService";
import * as customerService from "@/service/customerService";
import Treeselect from '@riophae/vue-treeselect';
import * as subscriptionUtil from "@/service/subscriptionUtil";
import { ManagementType } from "@/views/enums/subscriptionAction";
@Component({
  components: { Treeselect }
})
export default class RegisterSubscriptionModal extends BaseComponent {
  enumManagementType = ManagementType;
  datePickerFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
  @Prop({ required: true }) customerId: number;
  @Prop({ default: 0 }) meetingConcurrentParticipantsLimit_s;
  @Prop({ default: 0 }) numberOfConcurrentMeetings_s;
  @Emit("submitted") __submitted() { }

  subscriptionPlans = [];
  subscriptionPlanId: number = null;
  startTime: string = null;
  numberOfConcurrentMeetings: number = 0;
  meetingConcurrentParticipantsLimit: number = 0;
  formErrorMsg = "";
  isSubmitting = false;


  // Emit decorators only
  mounted() {
    this.startTime = moment().format("YYYY-MM-DD");
    this.fetchSubscriptionPlans();
  }


  async fetchSubscriptionPlans() {
    try {
      this.subscriptionPlans = await subscriptionPlanService.getPlans();

    } catch (e) {
      this.toastError(e);
    }
  }


  get subscriptionPlanOptions() {
    return this.subscriptionPlans.map(it => ({
      id: it.id,
      label: `${it.name} - ${this.formatCurrencyAmount(it.price)} VND / ${it.unitTimeVN === 'Ngày' ? it.numberOfDaysUsage : 1} ${it.unitTimeVN}`,
    }));
  }


  get selectedSubscriptionPlan() {
    return this.subscriptionPlans.find(it => it.id === this.subscriptionPlanId);
  }


  get startTimeMoment(): Moment {
    return moment.utc(this.startTime, "YYYY-MM-DD", true);
  }

  get shouldShowPricing() {
    return !!this.subscriptionPlanId;
  }


  get expDateStr(): string {
    return this.startTimeMoment.clone()
      .add(this.selectedSubscriptionPlan.numberOfDaysUsage, this.selectedSubscriptionPlan.unitTime)
      .format("DD/MM/YYYY");
  }

  // Hiển thị giá tiền chưa thuế
  get priceStr(): string {
    return this.formatCurrencyAmount(this.selectedSubscriptionPlan.price);
  }


  // Hiển thị thuế VAT
  get taxAmountStr(): string {
    return this.formatCurrencyAmount(
      new BigNumber(this.selectedSubscriptionPlan.price)
        .multipliedBy(0.1)
        .decimalPlaces(0)
        .toNumber()
    );
  }


  get planInfoRows() {
    const active = {
      maxParticipantPool: this.selectedSubscriptionPlan.maxParticipantPool,
      meetingConcurrentParticipantsLimit: this.meetingConcurrentParticipantsLimit,
      numberOfConcurrentMeetings: this.numberOfConcurrentMeetings
    };
    const rows = subscriptionUtil.buildPlanInfoRows(active, this.selectedSubscriptionPlan);
    if (this.selectedSubscriptionPlan.managementType === ManagementType.CONCURRENT_PARTICIPANT_POOL) {
      return rows.filter(row => row.label !== 'maxParticipantPool');
    } else {
      return rows.filter(row => row.label !== 'maximumNumberOfParticipantsInMeeting' && row.label !== 'numberOfConcurrentMeetings');
    }
  }

  get dueAmountStr(): string {
    return this.formatCurrencyAmount(
      new BigNumber(this.selectedSubscriptionPlan.price)
        .multipliedBy(1.1)
        .decimalPlaces(0)
        .toNumber()
    );
  }


  onClickCancel() {
    this.$bvModal.hide("register-subscription-modal");
  }


  subscriptionChange(id) {
    if (this.selectedSubscriptionPlan) {
      if (this.selectedSubscriptionPlan.managementType === ManagementType.CONCURRENT_PARTICIPANT_POOL) {
        this.numberOfConcurrentMeetings = 0;
        this.meetingConcurrentParticipantsLimit = this.selectedSubscriptionPlan.maxParticipantPool;
      } else {
        this.numberOfConcurrentMeetings = this.selectedSubscriptionPlan.numberOfConcurrentMeetings;
        this.meetingConcurrentParticipantsLimit = this.selectedSubscriptionPlan.maximumNumberOfParticipantsInMeeting;
      }
    }
  }


  async submitForm() {
    try {
      this.isSubmitting = true;
      const isPool = this.selectedSubscriptionPlan.managementType === ManagementType.CONCURRENT_PARTICIPANT_POOL;
      console.log("=== submitForm ===");
      console.log("selectedSubscriptionPlan:", this.selectedSubscriptionPlan);
      console.log("isPool:", isPool);
      console.log("this.numberOfConcurrentMeetings:", this.numberOfConcurrentMeetings);
      console.log("this.meetingConcurrentParticipantsLimit:", this.meetingConcurrentParticipantsLimit);
      
      const numMeetings = isPool ? null : (this.numberOfConcurrentMeetings === 0 ? this.selectedSubscriptionPlan.numberOfConcurrentMeetings : +this.numberOfConcurrentMeetings);
      const numParticipants = isPool ? null : (this.meetingConcurrentParticipantsLimit === 0 ? this.selectedSubscriptionPlan.maximumNumberOfParticipantsInMeeting : +this.meetingConcurrentParticipantsLimit);
      const maxPool = isPool ? +this.meetingConcurrentParticipantsLimit : null;
      console.log("parameters to send:", {
        customerId: this.customerId,
        subscriptionPlanId: this.subscriptionPlanId,
        numMeetings,
        numParticipants,
        startTime: this.startTimeMoment.toISOString(),
        maxPool
      });

      await customerService.newSubscription(
        this.customerId,
        this.subscriptionPlanId,
        numMeetings,
        numParticipants,
        this.startTimeMoment.toISOString(),
        maxPool
      );
      this.__submitted();
      this.$bvModal.hide("register-subscription-modal");
      this.toastSuccessDelay(this.$t("registerServiceSuccessful").toString());

    } catch (e) {
      this.toastError(e);
    } finally {
      this.isSubmitting = false;
    }
  }

}
</script>
